import {
  ref, toRefs, reactive, computed, onMounted, watch,
} from '@vue/composition-api';
import { from } from 'core-js/fn/array';
import {
  ServiceFormModel, ServiceActionValue, Field, DependantField, PrefilledField,
} from '@/app/shared/components/form/classes';
import { AppInfoService, Submit } from '@/app/shared/services/appInfoService/appInfoService';

export default function (props, { emit }) {
  console.log('[Form Composable] useForm composable initialized');
  console.log('[Form Composable] Initial props:', {
    formsExists: !!props.forms,
    formsIsArray: Array.isArray(props.forms),
    formsLength: props.forms ? props.forms.length : 0,
    forms: props.forms,
  });

  const data = reactive({
    formServices: [],
    results: [],
    dependantValues: [],
    showValidation: false,
    loading: false,
  });

  console.log('[Form Composable] Initial data state:', {
    formServices: data.formServices,
    formServicesLength: data.formServices.length,
  });

  const getResult = (form, fieldName) => {
    const result = data.results.filter(r => r.field === fieldName && r.serviceAction === (`${form.serviceName}/${form.action}`));

    if (result.length > 0) {
      return result[0];
    }
    return null;
  };

  const getFieldByName = (fieldName) => {
    let result;
    data.formServices.forEach((form) => {
      form.service.properties.forEach((field) => {
        if (field.name === fieldName) {
          result = field;
        }
      });
    });

    return result;
  };

  const getDisplayFields = (properties) => {
    const results = [];

    properties.forEach((property) => {
      if (property.attributes !== null) {
        property.attributes.forEach((attribute) => {
          if (attribute.includes('DisplayName')) {
            results.push(property);
          }
        });
      }
    });

    return results;
  };

  const getValidationErrors = (form, field) => {
    const errors = [];

    const result = getResult(form, field.name);

    const required = getAttributeValue(field.attributes, 'IsRequired');
    if (required && result === null) {
      errors.push('This field is required');
    }

    const minLength = getAttributeValue(field.attributes, 'MinimumLength');
    if (minLength !== undefined && result !== null && result.value.length < minLength) {
      errors.push(`Must be at least ${minLength} characters long`);
    }

    const maxLength = getAttributeValue(field.attributes, 'MaximumLength');
    if (maxLength !== undefined && result !== null && result.value.length >= maxLength) {
      errors.push(`Must be less than ${maxLength} characters long`);
    }

    const minValue = getAttributeValue(field.attributes, 'MinimumValue');
    if (minValue !== undefined && result !== null && result.value < minValue) {
      errors.push(`Value must be at least ${minValue}`);
    }

    const maxValue = getAttributeValue(field.attributes, 'MaximumValue');
    if (maxValue !== undefined && result !== null && result.value > maxValue) {
      errors.push(`Value must be less than ${maxValue}`);
    }

    const lessThanProperty = getAttributeValue(field.attributes, 'LessThanOrEqualToProperty');
    if (lessThanProperty !== undefined) {
      const targetResult = getResult(form, lessThanProperty);
      if (targetResult !== null && result !== null) {
        if (parseInt(result.value) > parseInt(targetResult.value)) {
          const target = getFieldByName(lessThanProperty);
          errors.push(`Value must be less than or equal to the ${getAttributeValue(target.attributes, 'DisplayName').toLowerCase()}(${targetResult.value})`);
        }
      }
    }

    return errors;
  };

  const searchForDependantFields = (service, action, result) => {
    dependantFields.value.forEach((field) => {
      if (service === field.service && action === field.action) {
        if (result[field.name] !== undefined) {
          data.dependantValues[field.bodyName] = result[field.name];
        }
      }
    });
  };

  const addInput = (inputValue, field, service, action) => {
    const serviceAction = `${service}/${action}`;

    const value = new ServiceActionValue(serviceAction, field, inputValue);

    for (let i = 0; i < data.results.length; i++) {
      if (data.results[i].serviceAction === serviceAction && data.results[i].field === field) {
        data.results.splice(i, 1);
      }
    }

    // if (inputValue.length > 0) {
    data.results.push(value);
    // }
  };

  const getServiceInfo = () => {
    console.log('[Form Composable] getServiceInfo called');
    console.log('[Form Composable] props.forms:', {
      exists: !!props.forms,
      isArray: Array.isArray(props.forms),
      length: props.forms ? props.forms.length : 0,
      forms: props.forms,
    });
    console.log('[Form Composable] data.formServices before:', {
      exists: !!data.formServices,
      isArray: Array.isArray(data.formServices),
      length: data.formServices ? data.formServices.length : 0,
    });

    // Ensure forms prop exists and is an array
    if (!props.forms || !Array.isArray(props.forms) || props.forms.length === 0) {
      console.warn('[Form Composable] getServiceInfo called but forms prop is empty or invalid');
      return;
    }

    console.log('[Form Composable] Processing', props.forms.length, 'forms');

    props.forms.forEach((form, index) => {
      console.log(`[Form Composable] Processing form ${index + 1}/${props.forms.length}:`, {
        serviceName: form ? form.serviceName : 'null',
        action: form ? form.action : 'null',
        displayOrder: form ? form.displayOrder : 'null',
        hasFields: form && form.fields ? form.fields.length : 0,
        hasPrefilled: form && form.prefilled ? form.prefilled.length : 0,
      });

      // Validate form structure
      if (!form || !form.serviceName || !form.action) {
        console.error('[Form Composable] Invalid form structure:', form);
        return;
      }

      console.log(`[Form Composable] Calling AppInfoService for ${form.serviceName}...`);
      AppInfoService(form.serviceName)
        .then((result) => {
          console.log(`[Form Composable] AppInfoService response for ${form.serviceName}:`, {
            hasResult: !!result,
            hasMethods: result && !!result.methods,
            methodsIsArray: result && Array.isArray(result.methods),
            methodsLength: result && result.methods ? result.methods.length : 0,
            result: result,
          });

          // Validate the response structure
          if (!result || !result.methods || !Array.isArray(result.methods)) {
            console.error(`[Form Composable] Invalid response from AppInfoService for ${form.serviceName}:`, result);
            return;
          }

          const service = result.methods.filter(s => s && s.name === form.action);
          console.log(`[Form Composable] Filtered services for action '${form.action}':`, {
            found: service.length,
            services: service,
          });

          if (!service || service.length === 0) {
            console.error(`[Form Composable] Service method '${form.action}' not found for ${form.serviceName}`);
            console.error(`[Form Composable] Available methods:`, result.methods.map(m => m.name));
            return;
          }

          form.service = service[0];
          console.log(`[Form Composable] Adding form to formServices:`, {
            serviceName: form.serviceName,
            action: form.action,
            hasService: !!form.service,
            serviceMethod: form.service ? form.service.method : 'none',
          });

          // Use Vue.set or direct assignment to ensure reactivity
          data.formServices.push(form);
          console.log(`[Form Composable] formServices length after push:`, data.formServices.length);

          // Shouldn't need to do this, we could set the index as the order but Vue fails to render this...
          if (Array.isArray(data.formServices)) {
            data.formServices.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            console.log(`[Form Composable] formServices sorted. Current length:`, data.formServices.length);
            
            // Force reactivity update by reassigning the array
            // This ensures Vue tracks the change properly
            const currentLength = data.formServices.length;
            console.log(`[Form Composable] Triggering reactivity update. Array length:`, currentLength);
          }
        })
        .catch((error) => {
          console.error(`[Form Composable] Error loading service info for ${form.serviceName}:`, error);
          // Log the full error for debugging in production
          if (error.response) {
            console.error('[Form Composable] Response status:', error.response.status);
            console.error('[Form Composable] Response data:', error.response.data);
            console.error('[Form Composable] Response headers:', error.response.headers);
          } else if (error.request) {
            console.error('[Form Composable] Request made but no response received:', error.request);
          } else {
            console.error('[Form Composable] Error setting up request:', error.message);
          }
          console.error('[Form Composable] Full error object:', error);
        });
    });

    console.log('[Form Composable] getServiceInfo completed. All API calls initiated.');
  };

  const getAttributeValue = (attributes, name) => {
    if (attributes !== null) {
      for (let i = 0; i < attributes.length; i++) {
        if (attributes[i].includes(name)) {
          return attributes[i].split(':')[1].substring(1);
        }
      }
    }
  };

  const totalValidationErrors = computed(() => {
    let total = 0;
    data.formServices.forEach((form) => {
      getDisplayFields(form.service.properties).forEach((field) => {
        total += getValidationErrors(form, field).length;
      });
    });

    emit('errorCount', total);
    return total;
  });

  const dependantFields = computed(() => {
    const fields = [];

    props.forms.forEach((form) => {
      form.fields.forEach((field) => {
        if (field instanceof DependantField) {
          fields.push(field);
        }
      });
    });

    return fields;
  });

  const submit = async () => {
    if (totalValidationErrors.value !== 0) {
      data.showValidation = true;
    } else {
      data.loading = true;
      data.showValidation = false;
      const groupedResult = data.results.reduce((r, a) => {
        r[a.serviceAction] = r[a.serviceAction] || [];
        r[a.serviceAction].push(a);
        return r;
      }, Object.create(null));

      for (let i = 0; i < props.forms.length; i++) {
        const form = props.forms[i];
        console.log(`[Form Composable] Processing form ${i + 1}/${props.forms.length}:`, {
          serviceName: form.serviceName,
          action: form.action,
          hasPrefilled: !!form.prefilled,
          prefilledType: typeof form.prefilled,
          prefilledIsArray: Array.isArray(form.prefilled),
          prefilledLength: form.prefilled ? form.prefilled.length : 0,
          prefilled: form.prefilled,
        });
        const group = groupedResult[`${form.serviceName}/${form.action}`] || [];
        const body = {};

        // Helper function to normalize field name to lowercase (first letter only)
        const normalizeFieldName = (name) => {
          if (!name) return name;
          // Convert first letter to lowercase (e.g., "Name" -> "name", "Interval" -> "interval")
          return name.charAt(0).toLowerCase() + name.slice(1);
        };

        // Helper function to set field value, preferring non-empty values
        const setFieldValue = (fieldName, value) => {
          const normalizedName = normalizeFieldName(fieldName);
          console.log(`[Form Composable] setFieldValue called: ${fieldName} (normalized: ${normalizedName}) = ${value} (type: ${typeof value})`);
          // If field doesn't exist, set it (even if undefined/null - let the server handle it)
          if (body[normalizedName] === undefined) {
            body[normalizedName] = value;
            console.log(`[Form Composable] Set ${normalizedName} = ${value}`);
          } else {
            // If existing value is empty/undefined and new value is not, use new value
            const existingValue = body[normalizedName];
            if ((existingValue === '' || existingValue === undefined || existingValue === null) 
                && (value !== '' && value !== undefined && value !== null)) {
              body[normalizedName] = value;
              console.log(`[Form Composable] Overwrote ${normalizedName} with ${value}`);
            } else {
              console.log(`[Form Composable] Kept existing value for ${normalizedName}: ${existingValue}`);
            }
            // Otherwise keep existing value (prefer first non-empty value)
          }
        };

        // Add fields from group results (normalize to lowercase)
        group.forEach((field) => {
          setFieldValue(field.field, field.value);
        });

        // Add fields from form definition (normalize to lowercase)
        form.fields.forEach((field) => {
          if (field instanceof Field) {
            setFieldValue(field.name, field.value);
          } else if (field instanceof DependantField) {
            setFieldValue(field.bodyName, data.dependantValues[field.bodyName]);
          }
        });

        // Add prefilled fields to body (for fields that don't have form inputs)
        // Note: TenantId preserves PascalCase, other fields are normalized to camelCase
        if (form.prefilled && Array.isArray(form.prefilled)) {
          console.log(`[Form Composable] Processing ${form.prefilled.length} prefilled fields for ${form.serviceName}/${form.action}:`, form.prefilled);
          form.prefilled.forEach((field) => {
            if (field instanceof PrefilledField) {
              // Exclude 'id' field for Create operations - server should generate it
              if (form.action === 'Create' && field.fieldName === 'id') {
                // Skip id field for create operations - server will generate it
                console.log(`[Form Composable] Skipping id field for Create operation`);
              } else {
                // TenantId preserves PascalCase, all other fields are normalized to camelCase
                let fieldName = field.fieldName;
                // Handle TenantId/tenantId for Clubs - convert to PascalCase
                if (fieldName === 'TenantId' || fieldName === 'tenantId') {
                  fieldName = 'TenantId';
                } else {
                  // Normalize other fields to camelCase (first letter lowercase, rest preserved)
                  fieldName = normalizeFieldName(fieldName);
                }
                console.log(`[Form Composable] Adding prefilled field: ${field.fieldName} -> ${fieldName} = ${field.value}`);
                if (body[fieldName] === undefined) {
                  body[fieldName] = field.value;
                } else if (body[fieldName] === '' || body[fieldName] === undefined || body[fieldName] === null) {
                  body[fieldName] = field.value;
                }
              }
            } else {
              console.warn(`[Form Composable] Prefilled field is not an instance of PrefilledField:`, field);
            }
          });
        } else {
          console.log(`[Form Composable] No prefilled fields found for ${form.serviceName}/${form.action} (prefilled: ${form.prefilled})`);
        }

        // Remove id field from Create operations (server should generate it)
        if (form.action === 'Create' && body.id !== undefined) {
          delete body.id;
        }

        // Remove duplicate fields with different casing
        // Preserve PascalCase for TenantId, preserve camelCase for contactDetailsId, normalize others
        const keysToRemove = [];
        const preserveCaseFields = ['TenantId', 'contactDetailsId']; // Fields to preserve exact casing
        
        Object.keys(body).forEach((key) => {
          const lowerKey = key.toLowerCase();
          if (key !== lowerKey) {
            // Check if this is a field we should preserve
            const shouldPreserve = preserveCaseFields.some(pf => 
              key === pf || key.toLowerCase() === pf.toLowerCase()
            );
            
            if (shouldPreserve) {
              // Find the correct casing from preserveCaseFields
              const correctCase = preserveCaseFields.find(pf => 
                key.toLowerCase() === pf.toLowerCase()
              );
              
              if (correctCase && key !== correctCase) {
                // Move value to correct casing and remove old key
                body[correctCase] = body[key];
                keysToRemove.push(key);
              } else if (body[lowerKey] !== undefined && key === correctCase) {
                // Remove lowercase version if correct case exists
                keysToRemove.push(lowerKey);
              }
            } else {
              // For other fields, normalize to camelCase (not all lowercase)
              // Only convert if it's all uppercase or has wrong casing
              const camelCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
              if (camelCaseKey !== key && camelCaseKey !== lowerKey) {
                if (body[camelCaseKey] !== undefined) {
                  keysToRemove.push(key);
                } else {
                  body[camelCaseKey] = body[key];
                  keysToRemove.push(key);
                }
              } else if (body[lowerKey] !== undefined && lowerKey !== camelCaseKey) {
                // If all lowercase version exists but we want camelCase, remove lowercase
                keysToRemove.push(lowerKey);
              }
            }
          } else {
            // Key is already all lowercase - check if we need to convert to camelCase
            // Only for known fields that should be camelCase
            if (key === 'contactdetailsid') {
              body['contactDetailsId'] = body[key];
              keysToRemove.push(key);
            } else if (key === 'tenantid') {
              body['TenantId'] = body[key];
              keysToRemove.push(key);
            }
          }
        });
        keysToRemove.forEach((key) => delete body[key]);

        // Ensure correct data types
        if (form.serviceName === 'Courses' && form.action === 'Create') {
          if (body.interval !== undefined && body.interval !== '') {
            body.interval = parseInt(body.interval, 10);
          }
          if (body.tenantId !== undefined && body.tenantId !== '') {
            body.tenantId = parseInt(body.tenantId, 10);
          }
        }
        
        if (form.serviceName === 'Clubs' && form.action === 'Create') {
          // Convert tenantId/tenantid to TenantId (PascalCase) - handle all variations
          const tenantIdValue = body.TenantId || body.tenantId || body.tenantid;
          if (tenantIdValue !== undefined && tenantIdValue !== '' && tenantIdValue !== null) {
            body.TenantId = parseInt(tenantIdValue, 10);
            // Remove any lowercase variations
            if (body.tenantId !== undefined) delete body.tenantId;
            if (body.tenantid !== undefined) delete body.tenantid;
          }
          
          // Ensure contactDetailsId is camelCase (not all lowercase) - handle all variations
          const contactDetailsIdValue = body.contactDetailsId || body.contactdetailsid;
          if (contactDetailsIdValue !== undefined) {
            body.contactDetailsId = contactDetailsIdValue;
            // Remove lowercase version if it exists
            if (body.contactdetailsid !== undefined) delete body.contactdetailsid;
          }
        }

        console.log(`${form.serviceName} - Started`);
        console.log('Payload:', JSON.stringify(body, null, 2));

        await Submit(`${form.serviceName}/${form.action}`, form.service.method, body).then((result) => {
          console.log(`${form.serviceName} - Finished`);
          searchForDependantFields(form.serviceName, form.action, result.result);

          if (i === (props.forms.length - 1)) {
            if (result.success) {
              emit('success');
              data.loading = false;
            }
          }
        });
      }
    }
  };

  onMounted(() => {
    console.log('[Form Composable] onMounted called');
    console.log('[Form Composable] Initial data state:', {
      formServices: data.formServices,
      formServicesLength: data.formServices ? data.formServices.length : 0,
      formsProp: props.forms,
      formsPropLength: props.forms ? props.forms.length : 0,
    });
    getServiceInfo();
    console.log('[Form Composable] getServiceInfo called from onMounted');
  });

  // Return using toRefs - this is the correct Vue 2 pattern
  // toRefs creates refs that auto-unwrap when accessed via this in templates/computed
  const returnValue = {
    ...toRefs(data),
    getResult,
    getFieldByName,
    getDisplayFields,
    getValidationErrors,
    submit,
    searchForDependantFields,
    addInput,
    getServiceInfo,
    getAttributeValue,
    totalValidationErrors,
    dependantFields,
  };

  console.log('[Form Composable] Returning from useForm:', {
    hasFormServices: !!returnValue.formServices,
    formServicesType: typeof returnValue.formServices,
    formServicesIsComputed: returnValue.formServices && typeof returnValue.formServices.value !== 'undefined',
    allKeys: Object.keys(returnValue),
  });

  return returnValue;
}
